import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";

export interface CommentMention {
  person_id: string;
  people: { id: string; first_name: string; last_name: string };
}

export interface Comment {
  id: string;
  entity_type: string;
  entity_id: string;
  parent_id: string | null;
  content: string;
  author_id: string;
  is_resolved: boolean;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  author: { id: string; first_name: string; last_name: string } | null;
  resolver: { id: string; first_name: string; last_name: string } | null;
  mentions: CommentMention[];
  replies: Comment[];
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroCommentRow = {
  id: string;
  entityType: string;
  entityID: string;
  parentID?: string | null;
  content: string;
  authorID: string;
  isResolved: boolean;
  resolvedBy?: string | null;
  resolvedAt?: number | string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  author?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  resolver?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  mentions?: Array<{
    personID: string;
    people?: {
      id: string;
      firstName: string;
      lastName: string;
    } | null;
  }> | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyPerson(
  person:
    | {
        id: string;
        firstName: string;
        lastName: string;
      }
    | null
    | undefined,
) {
  return person
    ? {
        id: person.id,
        first_name: person.firstName,
        last_name: person.lastName,
      }
    : null;
}

function toLegacyComment(row: ZeroCommentRow): Comment {
  return {
    id: row.id,
    entity_type: row.entityType,
    entity_id: row.entityID,
    parent_id: row.parentID ?? null,
    content: row.content,
    author_id: row.authorID,
    is_resolved: row.isResolved,
    resolved_by: row.resolvedBy ?? null,
    resolved_at: toLegacyTimestamp(row.resolvedAt) || null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    author: toLegacyPerson(row.author),
    resolver: toLegacyPerson(row.resolver),
    mentions: (row.mentions ?? [])
      .filter((mention) => !!mention.people)
      .map((mention) => ({
        person_id: mention.personID,
        people: toLegacyPerson(mention.people)!,
      })),
    replies: [],
  };
}

function buildCommentTree(rows: ZeroCommentRow[]) {
  const flat = rows.map(toLegacyComment);
  const byId = new Map(flat.map((comment) => [comment.id, comment]));
  const roots: Comment[] = [];

  for (const comment of flat) {
    if (comment.parent_id) {
      const parent = byId.get(comment.parent_id);
      if (parent) {
        parent.replies.push(comment);
        continue;
      }
    }
    roots.push(comment);
  }

  return roots;
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  onError,
}: {
  run: (input: TInput) => Promise<TResult>;
  onError: string;
}) {
  const [isPending, setIsPending] = useState(false);

  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) => toast.error(`${onError}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export function useComments(
  entityType: string | undefined,
  entityId: string | undefined,
) {
  const query =
    entityType && entityId
      ? appQueries.comments.byEntity({ entityType, entityID: entityId })
      : undefined;
  const [data, details] = useZeroQuery(query, {
    enabled: !!entityType && !!entityId,
  });
  const comments = buildCommentTree((data ?? []) as ZeroCommentRow[]);

  return {
    data: comments,
    isLoading:
      !!entityType && !!entityId && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateComment() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    {
      entityType: string;
      entityId: string;
      content: string;
      mentionedPersonIds: string[];
      parentId?: string;
    },
    string
  >({
    onError: "Failed to create comment",
    run: async ({
      entityType,
      entityId,
      content,
      mentionedPersonIds,
      parentId,
    }) => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.comments.create({
          id,
          entityType,
          entityID: entityId,
          parentID: parentId ?? null,
          content,
          mentionedPersonIDs: mentionedPersonIds,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Comment create rejected",
      );
      return id;
    },
  });
}

export function useDeleteComment() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, string>({
    onError: "Failed to delete comment",
    run: async (commentId) => {
      const mutation = zero.mutate(
        appMutators.comments.delete({ id: commentId }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Comment delete rejected",
      );
      return commentId;
    },
  });
}

export function useResolveComment() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, string>({
    onError: "Failed to resolve comment",
    run: async (commentId) => {
      const mutation = zero.mutate(
        appMutators.comments.resolve({ id: commentId }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Comment resolve rejected",
      );
      return commentId;
    },
  });
}

export function useUnresolveComment() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, string>({
    onError: "Failed to unresolve comment",
    run: async (commentId) => {
      const mutation = zero.mutate(
        appMutators.comments.unresolve({ id: commentId }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Comment unresolve rejected",
      );
      return commentId;
    },
  });
}
